import type { ReactElement } from 'react';
import { useViewportMetrics } from '@/hooks/dom';
import { Cscrollable, CROSS_AXIS_OVERFLOW } from '../Cscrollable';
import { useCmarkdownvisualMode } from '../Cmarkdownvisual';
import {
  TABLE_SCROLL_DIRECTION,
  TABLE_VISUAL_MODE,
} from './Cmarkdowntable.constants';
import { resolveCmarkdowntableBodyLayout } from './CmarkdowntableBodyLayout';
import { CmarkdowntableGrid } from './CmarkdowntableGrid';
import { CmarkdowntablePopover } from './CmarkdowntablePopover';
import type { CmarkdowntableBodyProps } from './Cmarkdowntable.types';
import { useCmarkdowntablePopover } from './useCmarkdowntablePopover';

export function CmarkdowntableBody({
  dataElScope,
  headerCells,
  rows,
  textSize,
  textWeight,
  textAlign,
  tableElementRef,
}: CmarkdowntableBodyProps): ReactElement {
  const { vh } = useViewportMetrics();
  const mode = useCmarkdownvisualMode();
  const isExpanded = mode !== TABLE_VISUAL_MODE.Compact;
  const popover = useCmarkdowntablePopover({ headerCells, mode });
  const layout = resolveCmarkdowntableBodyLayout({ isExpanded, textSize, textWeight });

  const tableGrid = (
    <CmarkdowntableGrid
      dataElScope={dataElScope}
      headerCells={headerCells}
      rows={rows}
      textAlign={textAlign}
      tableElementRef={tableElementRef}
      activeRowIndex={popover.activeRowIndex}
      hoverRowIndex={popover.hoverRowIndex}
      onHoverRowChange={popover.setHoverRowIndex}
      onOpenRow={popover.openPopoverForRow}
      onRowKeyDown={popover.handleRowKeyDown}
      headerPadding={layout.headerPadding}
      bodyPadding={layout.bodyPadding}
      headerCellContentStyle={layout.headerCellContentStyle}
      baseBodyCellContentStyle={layout.baseBodyCellContentStyle}
      baseBodyWeight={layout.baseBodyWeight}
    />
  );

  // One scrollport owns BOTH axes (vertical direction + crossAxisOverflow auto).
  // Nested horizontal-inside-vertical made the sticky header's containing block
  // the wrong scrollport, so the header scrolled away before the table ended
  // (1ipq15k / 02dc59s). A single overflow:auto box keeps sticky top pinned for
  // the full vertical scroll while still allowing horizontal pan.
  //
  // fadeSize={0} alone is NOT enough — Cscrollable still applies the default
  // verticalFadeTopSize (70) / verticalFadeBottomSize (100) mask, which dims the
  // sticky header row exactly like body text. Zero both vertical fades so the
  // header surface stays solid while content scrolls under it.
  return (
    <>
      <Cscrollable
        dataElScope={dataElScope}
        direction={TABLE_SCROLL_DIRECTION.Vertical}
        crossAxisOverflow={CROSS_AXIS_OVERFLOW.Auto}
        firstItemSpacing={0}
        fadeSize={0}
        horizontalFadeSize={48}
        verticalFadeTopSize={0}
        verticalFadeBottomSize={0}
        style={{
          ...layout.verticalScrollableStyle,
          ...layout.horizontalScrollableStyle,
          contain: 'none',
        }}
      >
        {tableGrid}
      </Cscrollable>

      <CmarkdowntablePopover
        dataElScope={dataElScope}
        activeRowIndex={popover.activeRowIndex}
        entries={popover.popoverEntries}
        idValue={popover.popoverIdValue}
        position={popover.popoverPosition}
        manualRect={popover.manualRect}
        didCopy={popover.didCopy}
        vh={vh}
        popoverRef={popover.popoverRef}
        onCopyEntries={popover.handleCopyEntries}
        onClose={popover.closePopover}
        onHeaderPointerDown={popover.handleHeaderPointerDown}
        onHeaderPointerMove={popover.handleHeaderPointerMove}
        onHeaderPointerEnd={popover.handleHeaderPointerEnd}
        onResizePointerDown={popover.handleResizePointerDown}
        onResizePointerMove={popover.handleResizePointerMove}
        onResizePointerEnd={popover.handleResizePointerEnd}
      />
    </>
  );
}
